/**
 * ReminderManager.service.ts
 * 
 * Service for managing goal reminders both in the app and using native notifications
 * when available through Capacitor.
 */

import { Capacitor } from '@capacitor/core';
import { Goal } from '../types/goals';
import { getLocalNotificationsModule } from './notifications.helper';
import logger from '../utils/logger';

// Helper method to check if running on native platform
const isNativePlatform = (): boolean => {
  try {
    return Capacitor.isNativePlatform();
  } catch (error) {
    // If Capacitor isn't available, assume we're on web
    logger.info('Capacitor not available, assuming web platform');
    return false;
  }
};

/**
 * Check if we have permission for notifications
 * Returns true if we have permission or if on web
 */
export const checkNotificationPermissions = async (): Promise<boolean> => {
  if (!isNativePlatform()) {
    // Always granted on web for our in-app notifications
    return true;
  }
  
  try {
    // Use our helper to safely import the module
    const { LocalNotifications } = await getLocalNotificationsModule();
    const permResult = await LocalNotifications.checkPermissions();
    return permResult.display === 'granted';
  } catch (error) {
    logger.error('Error checking notification permissions:', error);
    return false;
  }
};

/**
 * Request permission for notifications
 * Only relevant on native platforms
 */
export const requestNotificationPermissions = async (): Promise<boolean> => {
  if (!isNativePlatform()) {
    // No need to request on web
    return true;
  }
  
  try {
    // Use our helper to safely import the module
    const { LocalNotifications } = await getLocalNotificationsModule();
    const permResult = await LocalNotifications.requestPermissions();
    return permResult.display === 'granted';
  } catch (error) {
    logger.error('Error requesting notification permissions:', error);
    return false;
  }
};

/**
 * Schedule a reminder notification for a goal
 */
export const scheduleGoalReminder = async (
  goal: Goal,
  reminderTime: number,
  title: string = 'Goal Reminder',
  body: string = ''
): Promise<{ success: boolean; id?: number }> => {
  // Generate a formatted message if not provided
  const reminderBody = body || `Don't forget your goal "${goal.name}" (${Math.round(goal.progress)}% complete)`;
  
  // Create a unique ID for this reminder based on goal ID and timestamp
  const reminderId = parseInt(
    goal.id.substring(0, 8).replace(/[^0-9]/g, '').padEnd(8, '0').substring(0, 8)
  );
  
  // For web, we don't schedule actual system notifications
  if (!isNativePlatform()) {
    // Store in localStorage for our own in-app reminder system
    const existingReminders = JSON.parse(localStorage.getItem('GOAL_REMINDERS') || '[]');
    existingReminders.push({
      id: reminderId,
      goalId: goal.id,
      timestamp: reminderTime,
      title,
      body: reminderBody
    });
    localStorage.setItem('GOAL_REMINDERS', JSON.stringify(existingReminders));
    
    logger.info(`Web reminder scheduled for goal "${goal.name}" at ${new Date(reminderTime).toLocaleString()}`);
    return { success: true, id: reminderId };
  }
  
  // For native platforms, schedule a system notification
  try {
    // Use our helper to safely import the module
    const { LocalNotifications } = await getLocalNotificationsModule();
    
    // Schedule the notification
    await LocalNotifications.schedule({
      notifications: [
        {
          id: reminderId,
          title,
          body: reminderBody,
          schedule: { at: new Date(reminderTime) },
          sound: 'default',
          actionTypeId: 'GOAL_REMINDER',
          extra: {
            goalId: goal.id,
            type: 'goalReminder'
          }
        }
      ]
    });
    
    // Store notification ID in localStorage to track it for later cancellation
    const notificationIdsKey = `GOAL_NOTIFICATIONS_${goal.id}`;
    let notificationIds: number[] = [];
    
    // Retrieve existing IDs if any
    const storedIdsStr = localStorage.getItem(notificationIdsKey);
    if (storedIdsStr) {
      try {
        notificationIds = JSON.parse(storedIdsStr);
      } catch (e) {
        logger.error('Error parsing stored notification IDs:', e);
      }
    }
    
    // Add new ID if not already present
    if (!notificationIds.includes(reminderId)) {
      notificationIds.push(reminderId);
      localStorage.setItem(notificationIdsKey, JSON.stringify(notificationIds));
    }
    
    logger.info(`Native reminder scheduled for goal "${goal.name}" at ${new Date(reminderTime).toLocaleString()}`);
    return { success: true, id: reminderId };
  } catch (error) {
    logger.error('Error scheduling reminder notification:', error);
    return { success: false };
  }
};

/**
 * Cancel a specific goal reminder
 */
export const cancelGoalReminder = async (reminderId: number): Promise<boolean> => {
  if (!isNativePlatform()) {
    // For web, remove from localStorage
    const existingReminders = JSON.parse(localStorage.getItem('GOAL_REMINDERS') || '[]');
    const updatedReminders = existingReminders.filter((r: any) => r.id !== reminderId);
    localStorage.setItem('GOAL_REMINDERS', JSON.stringify(updatedReminders));
    return true;
  }
  
  // For native platforms, cancel the system notification
  try {
    // Use our helper to safely import the module
    const { LocalNotifications } = await getLocalNotificationsModule();
    await LocalNotifications.cancel({ notifications: [{ id: reminderId }] });
    
    // Remove the notification ID from storage for all goals
    // since it might be associated with any goal
    const goalKeys = Object.keys(localStorage)
      .filter(key => key.startsWith('GOAL_NOTIFICATIONS_'));
    
    for (const key of goalKeys) {
      try {
        const idsStr = localStorage.getItem(key);
        if (idsStr) {
          const ids = JSON.parse(idsStr) as number[];
          if (ids.includes(reminderId)) {
            // Remove this ID from the list
            const updatedIds = ids.filter(id => id !== reminderId);
            if (updatedIds.length > 0) {
              localStorage.setItem(key, JSON.stringify(updatedIds));
            } else {
              localStorage.removeItem(key);
            }
          }
        }
      } catch (e) {
        logger.error(`Error updating notification IDs for ${key}:`, e);
      }
    }
    
    return true;
  } catch (error) {
    logger.error('Error cancelling reminder notification:', error);
    return false;
  }
};

/**
 * Cancel all reminders for a specific goal
 */
export const cancelGoalReminders = async (goalId: string): Promise<boolean> => {
  if (!isNativePlatform()) {
    // For web, remove from localStorage
    const existingReminders = JSON.parse(localStorage.getItem('GOAL_REMINDERS') || '[]');
    const updatedReminders = existingReminders.filter((r: any) => r.goalId !== goalId);
    localStorage.setItem('GOAL_REMINDERS', JSON.stringify(updatedReminders));
    return true;
  }
  
  // For native platforms, retrieve notification IDs associated with this goal
  try {
    // Get all notification IDs for this goal from persistent storage
    const notificationIdsKey = `GOAL_NOTIFICATIONS_${goalId}`;
    let notificationIds: number[] = [];
    
    const storedIdsStr = localStorage.getItem(notificationIdsKey);
    if (storedIdsStr) {
      try {
        notificationIds = JSON.parse(storedIdsStr);
      } catch (e) {
        logger.error('Error parsing stored notification IDs:', e);
      }
    }
    
    // If we found notification IDs, cancel each one
    if (notificationIds.length > 0) {
      // Use our helper to safely import the module
      const { LocalNotifications } = await getLocalNotificationsModule();
      
      // Cancel all notifications for this goal
      for (const id of notificationIds) {
        await LocalNotifications.cancel({ notifications: [{ id }] });
      }
      
      // Clear notification IDs from storage
      localStorage.removeItem(notificationIdsKey);
      logger.info(`Cancelled ${notificationIds.length} reminders for goal ${goalId}`);
    } else {
      logger.info(`No stored notification IDs found for goal ${goalId}`);
    }
    
    return true;
  } catch (error) {
    logger.error('Error cancelling goal reminders:', error);
    return false;
  }
};

// Export the complete service
const exportedObject = {
  checkNotificationPermissions,
  requestNotificationPermissions,
  scheduleGoalReminder,
  cancelGoalReminder,
  cancelGoalReminders
};

export default exportedObject;